"use client"

import { useState, useEffect } from "react"
import {
  FaSearch,
  FaBox,
  FaDumbbell,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaRulerHorizontal,
  FaUser,
  FaClock,
  FaDownload,
} from "react-icons/fa"
import Sidebar from "../SidebarInAdminPanel/Sidebar"
import { fetchAllPosts } from "../../Controllers/postsController"

const PostsPage = () => {
  const [posts, setPosts] = useState([])
  const [filteredPosts, setFilteredPosts] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [filter, setFilter] = useState("all")
  const [loading, setLoading] = useState(true)

  const [selectedPost, setSelectedPost] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [expandedImage, setExpandedImage] = useState(null)

  const openModal = (post) => {
    setSelectedPost(post)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setSelectedPost(null)
    setIsModalOpen(false)
  }

  const handleImageClick = (imageUrl, e) => {
    e.stopPropagation() // Prevent opening the post modal
    setExpandedImage(imageUrl)
  }

  const handleCloseExpandedImage = () => {
    setExpandedImage(null)
  }

  const handleDownloadImage = (imageUrl, imageName, e) => {
    e.stopPropagation() // Prevent opening the post modal
    const link = document.createElement("a")
    link.href = imageUrl
    link.download = `${imageName}-post-image.jpg`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const fetchedPosts = await fetchAllPosts()

        // Transform Firestore data to match your UI structure
        const transformedPosts = fetchedPosts.map((post) => ({
          id: post.id,
          title: post.description || "No title",
          author: post.userEmail || "Unknown",
          size: post.size || "medium",
          weight: post.weight || "medium",
          from: post.pickupAddress || "Unknown location",
          to: post.dropoffAddress || "Unknown location",
          cost: `${post.estimatedCost || 0} RS`,
          distance: post.estimatedDistance || "0 km",
          isPublished: true, // Set your own logic here
          createdAt: post.createdAt?.toDate() || new Date(),
          category: "General",
          imageUrl: post.image || "/Images/default-post.jpeg",
        }))

        setPosts(transformedPosts)
        setFilteredPosts(transformedPosts)
      } catch (err) {
        console.error("Error loading posts:", err)
      } finally {
        setLoading(false)
      }
    }

    loadPosts()
  }, [])

  const publishedCount = posts.filter((post) => post.isPublished).length
  const draftCount = posts.length - publishedCount

  const handleFilter = (type) => {
    setFilter(type)
    if (type === "all") {
      setFilteredPosts(posts)
    } else if (type === "published") {
      setFilteredPosts(posts.filter((post) => post.isPublished))
    } else {
      setFilteredPosts(posts.filter((post) => !post.isPublished))
    }
  }

  const handleSearch = (e) => {
    setSearchTerm(e.target.value)
    const searchValue = e.target.value.toLowerCase()
    setFilteredPosts(
      posts.filter(
        (post) => post.title.toLowerCase().includes(searchValue) || post.author.toLowerCase().includes(searchValue),
      ),
    )
  }

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gradient-to-r from-[#0AD1C8] to-[#073235]">
      <Sidebar />
      <div className="flex-1 p-8">
        <h1 className="text-4xl font-bold mb-8 text-white">Posts</h1>
        <div className="bg-white p-6 rounded-xl shadow-md mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-blue-100 p-4 rounded-lg">
              <p className="text-lg font-semibold text-blue-800">Total Posts</p>
              <p className="text-3xl font-bold text-blue-600">{posts.length}</p>
            </div>
            <div className="bg-green-100 p-4 rounded-lg">
              <p className="text-lg font-semibold text-green-800">Published</p>
              <p className="text-3xl font-bold text-green-600">{publishedCount}</p>
            </div>
            <div className="bg-red-100 p-4 rounded-lg">
              <p className="text-lg font-semibold text-red-800">Draft</p>
              <p className="text-3xl font-bold text-red-600">{draftCount}</p>
            </div>
          </div>

          {/* Search bar and buttons */}
          <div className="flex flex-col md:flex-row justify-between items-center mb-6">
            {/* Search Bar */}
            <div className="relative w-full md:w-1/2 mb-4 md:mb-0">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by Title and Author"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>

            {/* Filter Buttons */}
            <div className="flex space-x-2">
              <button
                onClick={() => handleFilter("all")}
                className={`px-6 py-2 rounded-full text-sm font-medium transition ${
                  filter === "all" ? "bg-blue-500 text-white" : "bg-gray-200"
                }`}
              >
                All
              </button>
              <button
                onClick={() => handleFilter("published")}
                className={`px-6 py-2 rounded-full text-sm font-medium transition ${
                  filter === "published" ? "bg-green-500 text-white" : "bg-gray-200"
                }`}
              >
                Published
              </button>
              <button
                onClick={() => handleFilter("draft")}
                className={`px-6 py-2 rounded-full text-sm font-medium transition ${
                  filter === "draft" ? "bg-red-500 text-white" : "bg-gray-200"
                }`}
              >
                Draft
              </button>
            </div>
          </div>

          {/* Posts Grid - New Beautiful Design */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredPosts.map((post) => (
              <div
                key={post.id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border-2 border-gray-400 group hover:border-teal-500 hover:scale-[1.02] cursor-pointer"
                onClick={() => openModal(post)}
              >
                {/* Image Header with Hover Effects */}
                <div className="relative h-48 overflow-hidden group/image">
                  <img
                    src={post.imageUrl || "/placeholder.svg"}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>

                  {/* Image Hover Overlay with Search and Download buttons */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover/image:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                    <div className="flex space-x-3 opacity-0 group-hover/image:opacity-100 transition-opacity duration-300">
                      <button
                        onClick={(e) => handleImageClick(post.imageUrl, e)}
                        className="bg-white p-3 rounded-full hover:bg-teal-100 transition-colors shadow-lg"
                        aria-label="View image"
                      >
                        <FaSearch className="text-teal-600 text-lg" />
                      </button>
                      <button
                        onClick={(e) => handleDownloadImage(post.imageUrl, post.title, e)}
                        className="bg-white p-3 rounded-full hover:bg-teal-100 transition-colors shadow-lg"
                        aria-label="Download image"
                      >
                        <FaDownload className="text-teal-600 text-lg" />
                      </button>
                    </div>
                  </div>

                  <div className="absolute top-4 right-4">
                    <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-gray-700">
                      {post.isPublished ? "Published" : "Draft"}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Title and Author */}
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-gray-800 mb-2 line-clamp-2 leading-tight">{post.title}</h3>
                    <div className="flex items-center text-gray-500 text-sm">
                      <FaUser className="mr-2" />
                      <span>{post.author}</span>
                      <FaClock className="ml-4 mr-2" />
                      <span>{new Date(post.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* Size and Weight Tags */}
                  <div className="flex gap-2 mb-4">
                    <div className="flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm">
                      <FaBox className="mr-1 text-xs" />
                      <span className="font-medium">{post.size}</span>
                    </div>
                    <div className="flex items-center bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-sm">
                      <FaDumbbell className="mr-1 text-xs" />
                      <span className="font-medium">{post.weight}</span>
                    </div>
                  </div>

                  {/* Route Section */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-4">
                    <div className="space-y-3">
                      {/* From */}
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-3 h-3 bg-green-500 rounded-full mt-1 mr-3 ring-4 ring-green-100"></div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">From</p>
                          <p className="text-sm text-gray-800 font-medium truncate">{post.from}</p>
                        </div>
                      </div>

                      {/* Connecting Line */}
                      <div className="ml-1.5 w-0.5 h-4 bg-gradient-to-b from-green-500 to-red-500"></div>

                      {/* To */}
                      <div className="flex items-start">
                        <FaMapMarkerAlt className="flex-shrink-0 text-red-500 mt-1 mr-3 text-sm" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">To</p>
                          <p className="text-sm text-gray-800 font-medium truncate">{post.to}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cost and Distance */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 text-center">
                      <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-2">
                        <FaMoneyBillWave className="text-white text-sm" />
                      </div>
                      <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wide">Cost</p>
                      <p className="text-lg font-bold text-emerald-800">{post.cost}</p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center">
                      <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-2">
                        <FaRulerHorizontal className="text-white text-sm" />
                      </div>
                      <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide">Distance</p>
                      <p className="text-lg font-bold text-blue-800">{post.distance}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredPosts.length === 0 && !loading && (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaBox className="text-gray-400 text-2xl" />
              </div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No posts found</h3>
              <p className="text-gray-500">Try adjusting your search or filter criteria.</p>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden animate-pulse">
                  <div className="h-48 bg-gray-200"></div>
                  <div className="p-6">
                    <div className="h-6 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                    <div className="flex gap-2 mb-4">
                      <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                      <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                    </div>
                    <div className="h-20 bg-gray-200 rounded-xl mb-4"></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="h-16 bg-gray-200 rounded-xl"></div>
                      <div className="h-16 bg-gray-200 rounded-xl"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Post Detail Modal */}
          {isModalOpen && selectedPost && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                {/* Modal Header with Image Hover Effects */}
                <div className="relative group/modalimage">
                  <img
                    src={selectedPost.imageUrl || "/placeholder.svg"}
                    alt={selectedPost.title}
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>

                  {/* Modal Image Hover Overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover/modalimage:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                    <div className="flex space-x-3 opacity-0 group-hover/modalimage:opacity-100 transition-opacity duration-300">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleImageClick(selectedPost.imageUrl, e)
                        }}
                        className="bg-white p-3 rounded-full hover:bg-teal-100 transition-colors shadow-lg"
                        aria-label="View image"
                      >
                        <FaSearch className="text-teal-600 text-lg" />
                      </button>
                      <button
                        onClick={(e) => handleDownloadImage(selectedPost.imageUrl, selectedPost.title, e)}
                        className="bg-white p-3 rounded-full hover:bg-teal-100 transition-colors shadow-lg"
                        aria-label="Download image"
                      >
                        <FaDownload className="text-teal-600 text-lg" />
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={closeModal}
                    className="absolute top-4 right-4 bg-white/90 hover:bg-white rounded-full p-2 transition-colors z-10"
                  >
                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <div className="absolute bottom-4 left-6 text-white z-10">
                    <h2 className="text-3xl font-bold mb-2">{selectedPost.title}</h2>
                    <div className="flex items-center text-white/90">
                      <FaUser className="mr-2" />
                      <span className="mr-4">{selectedPost.author}</span>
                      <FaClock className="mr-2" />
                      <span>{new Date(selectedPost.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>

                {/* Modal Content */}
                <div className="p-8">
                  {/* Status and Tags */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex gap-3">
                      <span
                        className={`px-4 py-2 rounded-full text-sm font-semibold ${
                          selectedPost.isPublished ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                        }`}
                      >
                        {selectedPost.isPublished ? "Published" : "Draft"}
                      </span>
                      <span className="bg-gray-100 text-gray-800 px-4 py-2 rounded-full text-sm font-semibold">
                        {selectedPost.category}
                      </span>
                    </div>
                  </div>

                  {/* Package Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                    <div className="space-y-6">
                      <h3 className="text-2xl font-bold text-gray-800 mb-4">Package Details</h3>

                      <div className="bg-blue-50 rounded-xl p-6">
                        <div className="flex items-center mb-3">
                          <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mr-4">
                            <FaBox className="text-white text-lg" />
                          </div>
                          <div>
                            <p className="text-blue-600 font-semibold text-sm uppercase tracking-wide">Package Size</p>
                            <p className="text-2xl font-bold text-blue-800">{selectedPost.size}</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-purple-50 rounded-xl p-6">
                        <div className="flex items-center mb-3">
                          <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mr-4">
                            <FaDumbbell className="text-white text-lg" />
                          </div>
                          <div>
                            <p className="text-purple-600 font-semibold text-sm uppercase tracking-wide">
                              Package Weight
                            </p>
                            <p className="text-2xl font-bold text-purple-800">{selectedPost.weight}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <h3 className="text-2xl font-bold text-gray-800 mb-4">Delivery Information</h3>

                      <div className="bg-emerald-50 rounded-xl p-6">
                        <div className="flex items-center mb-3">
                          <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center mr-4">
                            <FaMoneyBillWave className="text-white text-lg" />
                          </div>
                          <div>
                            <p className="text-emerald-600 font-semibold text-sm uppercase tracking-wide">
                              Estimated Cost
                            </p>
                            <p className="text-2xl font-bold text-emerald-800">{selectedPost.cost}</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-blue-50 rounded-xl p-6">
                        <div className="flex items-center mb-3">
                          <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mr-4">
                            <FaRulerHorizontal className="text-white text-lg" />
                          </div>
                          <div>
                            <p className="text-blue-600 font-semibold text-sm uppercase tracking-wide">Distance</p>
                            <p className="text-2xl font-bold text-blue-800">{selectedPost.distance}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Route Details */}
                  <div className="bg-gray-50 rounded-2xl p-8">
                    <h3 className="text-2xl font-bold text-gray-800 mb-6">Route Details</h3>
                    <div className="space-y-6">
                      {/* From Location */}
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full mt-1 mr-4 ring-4 ring-green-100 flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        </div>
                        <div className="flex-1">
                          <p className="text-green-600 font-semibold text-sm uppercase tracking-wide mb-1">
                            Pickup Location
                          </p>
                          <p className="text-xl font-semibold text-gray-800">{selectedPost.from}</p>
                          <p className="text-gray-600 mt-1">Starting point for delivery</p>
                        </div>
                      </div>

                      {/* Connecting Line */}
                      <div className="ml-3 w-0.5 h-12 bg-gradient-to-b from-green-500 to-red-500"></div>

                      {/* To Location */}
                      <div className="flex items-start">
                        <FaMapMarkerAlt className="flex-shrink-0 text-red-500 mt-2 mr-4 text-xl" />
                        <div className="flex-1">
                          <p className="text-red-600 font-semibold text-sm uppercase tracking-wide mb-1">
                            Delivery Location
                          </p>
                          <p className="text-xl font-semibold text-gray-800">{selectedPost.to}</p>
                          <p className="text-gray-600 mt-1">Final destination for delivery</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                 
                </div>
              </div>
            </div>
          )}

          {/* Image Expanded Modal */}
          {expandedImage && (
            <div
              className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
              onClick={handleCloseExpandedImage}
            >
              <div className="relative max-w-4xl max-h-[90vh] w-full">
                <button
                  onClick={handleCloseExpandedImage}
                  className="absolute top-4 right-4 bg-white rounded-full p-2 text-gray-800 hover:bg-gray-200 transition-colors z-10"
                  aria-label="Close"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <img
                  src={expandedImage || "/placeholder.svg"}
                  alt="Expanded view"
                  className="max-h-[85vh] max-w-full mx-auto object-contain rounded-lg"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PostsPage
